import { useRequest } from 'ahooks';
import { Layout, Table, Tabs } from 'antd';
import { LinkButton } from 'components';
import LocaleContext from 'locales';
import moment from 'moment';
import Detail from 'pages/Request/Detail';
import React, { useContext } from 'react';
import { getApprovalDocuments, getCompletedApprovalDocuments } from 'services/websocket/approval';
import { approvalLineStates } from 'utils/types';

/**
 * 승인 업무
 * @returns ReactNode
 */
function Approval(props) {
  const { location } = props;
  const { locale } = useContext(LocaleContext);

  // states
  const [detail, setDetail] = React.useState(undefined); // 상세 페이지 정보
  const [activeTabKey, setActiveTabKey] = React.useState('wait'); // 활성탭 키

  // variables
  const userId = localStorage.getItem('userId'); // 로그한 사용자 ID

  // 목록 요청의 payload
  const defaultParams = {
    wait: { paging: { filter: { state: 'approval.state.waiting', currentApproverId: userId } } },
    complete: {
      paging: {
        filter: {
          status: ['approval.state.approved', 'approval.state.rejected'],
          approverId: userId,
        },
      },
    },
  };

  // 대기중 목록 조회
  const fetchWaitList = useRequest(getApprovalDocuments, {
    ready: activeTabKey === 'wait',
    defaultParams: [defaultParams.wait],
  });

  // 완료 목록 조회
  const fetchCompleteList = useRequest(getCompletedApprovalDocuments, {
    ready: activeTabKey === 'complete',
    defaultParams: [defaultParams.complete],
  });

  // 항목 클릭 핸들러 - 대기중 탭
  const handleWaitItemClick = (record) => {
    setDetail({
      id: record.id,
      type: record.type,
    });
  };

  // 항목 클릭 핸들러 - 완료 탭
  const handleCompletedItemClick = (record) => {
    setDetail({
      id: record.requestId, // 완료 탭에서는 requestId 사용
      type: undefined, // 완료 목록에는 type 정보 없음. Detail에서 API 호출 후 결정
      isCompleted: true,
    });
  };

  const onDetailClose = () => {
    setDetail(undefined);
    if (activeTabKey === 'wait') {
      fetchWaitList.runAsync(defaultParams.wait);
    } else {
      fetchCompleteList.runAsync(defaultParams.complete);
    }
  };

  return (
    <Layout.Content>
      <Tabs activeKey={activeTabKey} onChange={(activeKey) => setActiveTabKey(activeKey)}>
        <Tabs.TabPane tab={locale.resource['txt.approval.waiting']} key="wait">
          <Table
            rowKey="id"
            size="middle"
            loading={fetchWaitList.loading}
            dataSource={fetchWaitList.data || []}
            columns={[
              {
                title: locale.resource['txt.contents'],
                dataIndex: 'type',
                width: 180,
                render: (text, record) => (
                  <LinkButton onClick={() => handleWaitItemClick(record)}>
                    {locale.resource[text]}
                  </LinkButton>
                ),
              },
              {
                title: locale.resource['txt.applicant'],
                dataIndex: 'applicant',
                width: 120,
              },
              {
                title: locale.resource['txt.status'],
                dataIndex: 'state',
                width: 120,
                render: (text) => locale.resource[text],
              },
              {
                title: locale.resource[''],
                dataIndex: 'reason',
              },
              {
                title: locale.resource['txt.requested-date'],
                dataIndex: 'applyDate',
                width: 180,
                render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss'),
              },
            ]}
          />
        </Tabs.TabPane>
        <Tabs.TabPane tab={locale.resource['txt.approval.complete']} key="complete">
          <Table
            rowKey="id"
            size="middle"
            loading={fetchCompleteList.loading}
            dataSource={fetchCompleteList.data || []}
            columns={[
              {
                title: `${locale.resource['txt.applicant']}(${locale.resource['txt.id']})`,
                dataIndex: 'applicant',
                width: 200,
                ellipsis: true,
                render: (text, record) => (
                  <LinkButton onClick={() => handleCompletedItemClick(record)}>
                    {`${record.applicantName}(${text})`}
                  </LinkButton>
                ),
              },
              {
                title: locale.resource['txt.status'],
                dataIndex: 'Status',
                width: 80,
                render: (text) => locale.resource[approvalLineStates[text]],
              },
              {
                title: locale.resource['txt.column.reason'],
                dataIndex: 'Reason',
              },
              {
                title: locale.resource['txt.approval-date'],
                dataIndex: 'ApprovalDate',
                width: 180,
                render: (text) => moment(text).format('YYYY-MM-DD HH:mm:ss'),
              },
            ]}
          />
        </Tabs.TabPane>
      </Tabs>
      <Detail
        id={detail?.id}
        type={detail?.type}
        isCompleted={detail?.isCompleted}
        onDetailClose={onDetailClose}
        location={location}
      />
    </Layout.Content>
  );
}

export default Approval;
